<template>
  <LazyHydrate v-bind="hydrationStrategy" @hydrated="onHydration()">
    <header
      v-bind="$attrs"
      :id="`header${blok._uid}`"
      class="sticky top-0 z-30"
    >
      <ResponsiveWrapper :component="blok.component" :devices="devices">
        <div class="w-full">
          <BackgroundWrapper :blok="blok" class="relative">
            <div
              v-if="showMobileMenu"
              class="absolute h-screen w-screen bg-black opacity-50"
            />

            <WidthWrapper
              :width="blok.useTextWidth ? 'text' : 'full'"
              class="relative"
            >
              <div
                class="flex items-center justify-between px-3 py-3 lg:justify-start lg:space-x-10"
                :class="!blok.useTextWidth ? 'sm:px-6' : 'sm:px-0'"
              >
                <div class="flex h-8 justify-start sm:h-10 lg:w-0 lg:flex-1">
                  <a
                    v-if="blok.logoLinksToHome"
                    :href="homeLink"
                    class="h-fit"
                    :class="linkClass"
                    aria-label="Homepage"
                  >
                    <ImgAdapter
                      :img-sources="[blok.logoImage, blok.logoUrl]"
                      :height="blok.oversizedLogo ? 100 : 50"
                      class="h-8 w-auto sm:h-10"
                      :class="[
                        marginClass,
                        {
                          'oversized origin-top-left lg:scale-200':
                            blok.oversizedLogo,
                        },
                      ]"
                      :alt="logoAlt"
                      :lazy="false"
                    />
                  </a>
                  <ImgAdapter
                    v-else
                    :img-sources="[blok.logoImage, blok.logoUrl]"
                    :height="blok.oversizedLogo ? 100 : 50"
                    class="h-8 w-auto sm:h-10"
                    :class="[
                      marginClass,
                      {
                        'oversized origin-top-left lg:scale-200':
                          blok.oversizedLogo,
                      },
                    ]"
                    :alt="logoAlt"
                    :lazy="false"
                  />
                </div>
                <div v-if="menuItems.length" class="-my-2 -mr-2 lg:hidden">
                  <button
                    ref="openMenuButton"
                    type="button"
                    class="inline-flex items-center justify-center rounded-md p-2 focus:outline-2 focus:outline-offset-[-2px] focus:outline-current"
                    :class="linkClass"
                    aria-controls="mobileMenu"
                    aria-haspopup="true"
                    :aria-expanded="showMobileMenu ? 'true' : 'false'"
                    @click="openMobileMenu"
                  >
                    <span class="text-md mr-1 inline-block uppercase"
                      >Menu</span
                    >
                    <Bars3Icon class="h-6 w-6" />
                  </button>
                </div>
                <nav class="hidden lg:flex" aria-label="primaire navigatie">
                  <ul class="flex items-center space-x-10">
                    <li
                      v-for="(item, index) in menuItems"
                      :key="item._uid"
                      :class="{
                        'mr-1':
                          item.component === 'HeaderSubMenuWithAuth' &&
                          index === menuItems.length - 2,
                      }"
                    >
                      <component
                        :is="item.component"
                        :item="item"
                        :blok="blok"
                        :context="context"
                        :link-class="linkClass"
                        :is-last-item="index === menuItems.length - 1"
                      />
                    </li>
                  </ul>
                </nav>
              </div>

              <!--
    Mobile menu, show/hide based on mobile menu state.

    Entering: "duration-200 ease-out"
      From: "opacity-0 scale-95"
      To: "opacity-100 scale-100"
    Leaving: "duration-100 ease-in"
      From: "opacity-100 scale-100"
      To: "opacity-0 scale-95"
  -->
              <div
                class="absolute inset-x-0 top-0 z-1080 max-h-screen origin-top-right overflow-y-visible drop-shadow-xl transition lg:hidden"
                :class="[
                  {
                    'scale-y-70 opacity-0 duration-200 ease-out':
                      !showMobileMenu,
                    'scale-y-100 opacity-100 duration-100 ease-in':
                      showMobileMenu,
                  },
                  showMobileMenu
                    ? 'pointer-events-auto'
                    : 'pointer-events-none',
                ]"
              >
                <div :class="backgroundColorClass(blok.bgVariant)">
                  <FocusTrap :is-active="showMobileMenu">
                    <div class="px-3 pt-2 pb-3">
                      <div class="flex items-center justify-between">
                        <div class="h-8">
                          <ImgAdapter
                            :img-sources="[blok.logoImage, blok.logoUrl]"
                            :height="50"
                            class="h-8 w-auto"
                            :alt="logoAlt"
                            :lazy="false"
                          />
                        </div>
                        <div class="-mr-2">
                          <button
                            type="button"
                            class="inline-flex items-center justify-center rounded-md p-2 focus:outline-2 focus:outline-offset-[-2px] focus:outline-current"
                            :class="linkClass"
                            aria-controls="mobileMenu"
                            aria-label="Sluit menu"
                            @click="closeMobileMenu(true)"
                          >
                            <XMarkIcon class="h-6 w-6" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="px-4 pb-6">
                      <nav
                        :class="{ hidden: !showMobileMenu }"
                        aria-label="primaire navigatie"
                      >
                        <ul
                          id="mobileMenu"
                          class="flex flex-col overflow-y-auto"
                        >
                          <li
                            v-for="item in menuItems"
                            :key="item._uid"
                            class="space-between relative flex items-center border-b py-4"
                          >
                            <component
                              :is="item.component"
                              :item="item"
                              :blok="blok"
                              :context="context"
                              :link-class="`${linkClass} w-full pr-8 lg:pr-0 lg:w-auto text-xl font-bold`"
                              class="grow"
                              @click.capture="
                                () =>
                                  item.component !== 'HeaderSubMenu' &&
                                  item.component !== 'HeaderSubMenuWithAuth' &&
                                  item.component !== 'HeaderFavorites' &&
                                  closeMobileMenu()
                              "
                              @keypress.enter="
                                () =>
                                  item.component !== 'HeaderSubMenu' &&
                                  item.component !== 'HeaderSubMenuWithAuth' &&
                                  item.component !== 'HeaderFavorites' &&
                                  closeMobileMenu()
                              "
                            />

                            <ArrowRightIcon
                              v-if="item.component === 'CallToActionLink'"
                              aria-hidden="true"
                              class="absolute right-0 h-6 w-6"
                              :class="linkClass"
                            />
                          </li>

                          <li v-if="!bottomTextEmpty" class="pt-8 pb-4">
                            <RichText
                              :blok="blok"
                              class="richtext-link default"
                              :rich-text-content="blok.bottomText"
                              reset-styles
                            />
                          </li>

                          <li v-if="blok.whatsappButton?.length" class="py-4">
                            <WhatsappButton
                              :item="blok.whatsappButton[0]"
                              @click.capture="closeMobileMenu"
                              @keypress.enter="closeMobileMenu"
                            />
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </FocusTrap>
                </div>
              </div>
            </WidthWrapper>
          </BackgroundWrapper>
        </div>
      </ResponsiveWrapper>
    </header>
  </LazyHydrate>
</template>

<script setup>
import { ref, computed } from 'vue'
import CallToActionLink from '../components/CallToActionLink.vue'
import CallToActionButton from '../components/CallToActionButton.vue'
import HeaderSubMenu from '../components/HeaderSubMenu.vue'
import WhatsappButton from '../components/WhatsappButton.vue'
import ResponsiveWrapper from '../components/ResponsiveWrapper.vue'
import BackgroundWrapper from '../components/BackgroundWrapper.vue'
import WidthWrapper from '../components/WidthWrapper.vue'
import ImgAdapter from '../components/ImgAdapter.vue'
import FocusTrap from '../components/FocusTrap.vue'
import RichText from '../components/RichText.vue'
import { usePageContext } from '../renderer/usePageContext'
import { Bars3Icon, XMarkIcon, ArrowRightIcon } from '@heroicons/vue/24/solid'
import { renderRichText } from '@storyblok/vue'
import { useHead } from '@unhead/vue'
import { useColorPalette } from '../composables/colorPalette'
import HeaderSubMenuWithAuth from '../components/HeaderSubMenuWithAuth.vue'
import HeaderFavorites from '../components/HeaderFavorites.vue'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
})
// All of these components can be added dynamically to header
defineOptions({
  components: {
    CallToActionButton,
    CallToActionLink,
    HeaderSubMenu,
    HeaderSubMenuWithAuth,
    HeaderFavorites,
  },
})

const { backgroundColorClass, textColorClass, isBackgroundDark } =
  useColorPalette()

const openMenuButton = ref(null)
const showMobileMenu = ref(false)

const hydrationStrategy = computed(() => {
  return { whenIdle: true }
})
const menuItems = computed(() => {
  const items = props.blok.menuItems
  if (props.blok.displayFavorites) {
    // If last item is auth, insert this before that?
    items.push({
      component: 'HeaderFavorites',
    })
  }
  return items
})
const homeLink = computed(() => {
  if (import.meta.env.VITE_IS_EDITOR) {
    return props.context && '/' + props.context.domainSlug
  }
  return '/'
})
const marginClass = computed(() => {
  const marginMode = props.blok.logoVerticalMargin

  if (marginMode === 'none') {
    return ''
  }
  if (marginMode === 'sm') {
    return 'my-0.5'
  }
  if (marginMode === 'md') {
    return 'my-1'
  }
  if (marginMode === 'lg') {
    return 'my-2'
  }
  if (marginMode === 'xl') {
    return 'my-3'
  }

  return ''
})
const linkClass = computed(() => {
  if (props.blok.linkColor) {
    return textColorClass(props.blok.linkColor)
  }
  return isBackgroundDark(props.blok.bgVariant)
    ? 'text-gray-100 hover:text-white focus:text-white'
    : 'text-gray-800 hover:text-gray-900 focus:text-gray-900'
})
const logoAlt = computed(() => {
  return props.context?.site?.name ? `${props.context.site.name} logo` : ''
})
const bottomTextEmpty = computed(() => {
  if (props.blok.bottomText) {
    if (typeof props.blok.bottomText === 'string') {
      return !props.blok.bottomText
    }
    const renderedText = renderRichText(props.blok.bottomText)
    return !renderedText || renderedText === '<p></p>'
  }

  return true
})
const devices = computed(() => {
  // Since we added responsiveness much later after the block was created, if this prop is not
  // defined or it doesn't include at least one device, display it in all the resolutions
  if (Array.isArray(props.blok.devices) && props.blok.devices.length > 0) {
    return props.blok.devices
  } else {
    return ['mobile', 'tablet', 'desktop']
  }
})

function onHydration() {
  // Add CSS class to header when the page has been scrolled, useful for changing the header style in that case
  const headerElement = document.getElementById(`header${props.blok._uid}`)

  const observer = new IntersectionObserver(
    ([e]) => {
      e.target.classList.toggle('scrolled', e.intersectionRatio === 1)
    },
    {
      threshold: 1.0,
      root: document.getElementById('jrCB'),
      rootMargin: `-${headerElement.clientHeight}px 0px 0px 0px`,
    },
  )
  observer.observe(headerElement)
}
function openMobileMenu() {
  showMobileMenu.value = true
}
function closeMobileMenu(changeFocus) {
  showMobileMenu.value = false
  if (changeFocus) {
    openMenuButton.value.focus()
  }
}

const pageContext = usePageContext()
useHead(
  () => {
    return {
      // If we don't add this, when you use anchors, the content appears below the header.
      style: [
        {
          key: 'headerScrollPadding',
          nonce: pageContext.nonce,
          innerHTML: `
            html {
              scroll-padding-top: 3.75rem;
            }

            @media (min-width: 576px) {
              html {
                scroll-padding-top: 4.5rem;
              }
            }`,
        },
      ],
    }
  },
  { mode: import.meta.env.VITE_IS_EDITOR ? 'client' : 'server' },
)
</script>

<style>
@reference "../css/theme.css";

.scrolled .oversized {
  @apply lg:scale-100 lg:transition-all lg:duration-100;
}

#mobileMenu {
  max-height: calc(100vh - 3.75rem);
}
</style>
